import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import { useRole } from '../hooks/useRole';
import { createApiClient, bazaarService, classifiedService } from '../services/api';
import { ModerationFlag } from '../types';
import type { ModerationQueueItem } from '../types';

const FLAG_LABELS: Record<ModerationFlag, string> = {
  [ModerationFlag.Clean]: 'Clean',
  [ModerationFlag.Abusive]: 'Abusive',
  [ModerationFlag.Spam]: 'Spam',
  [ModerationFlag.Fraud]: 'Fraud',
};
const FLAG_COLOURS: Record<ModerationFlag, string> = {
  [ModerationFlag.Clean]: 'bg-green-100 text-green-800',
  [ModerationFlag.Abusive]: 'bg-red-100 text-red-800',
  [ModerationFlag.Spam]: 'bg-amber-100 text-amber-800',
  [ModerationFlag.Fraud]: 'bg-orange-100 text-orange-800',
};

type Source = 'bazaar' | 'classified';
type QueueItem = ModerationQueueItem & { source: Source };

export default function AdminBazaarModerationPage() {
  const { getToken } = useAppAuth();
  const { isAdminOrOps, isRoleLoading } = useRole();
  const qc = useQueryClient();
  const [flagFilter, setFlagFilter] = useState<ModerationFlag | 'all'>('all');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const { data: bazaarQueue = [], isLoading: loadingBazaar } = useQuery({
    queryKey: ['admin-bazaar-moderation'],
    queryFn: () => bazaarService(createApiClient(getToken)).getModerationQueue(),
    enabled: isAdminOrOps,
  });
  const { data: classifiedQueue = [], isLoading: loadingClassified } = useQuery({
    queryKey: ['admin-classified-moderation'],
    queryFn: () => classifiedService(createApiClient(getToken)).getModerationQueue(),
    enabled: isAdminOrOps,
  });

  const queue: QueueItem[] = [
    ...bazaarQueue.map(i => ({ ...i, source: 'bazaar' as const })),
    ...classifiedQueue.map(i => ({ ...i, source: 'classified' as const })),
  ];
  const visible = flagFilter === 'all' ? queue : queue.filter(i => i.flag === flagFilter);

  const decisionMutation = useMutation({
    mutationFn: ({ item, action, reason: r }: { item: QueueItem; action: 'approve' | 'reject'; reason?: string }) => {
      const client = createApiClient(getToken);
      const svc = item.source === 'bazaar' ? bazaarService(client) : classifiedService(client);
      return svc.moderate(item.listingId, action, r);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-bazaar-moderation'] });
      qc.invalidateQueries({ queryKey: ['admin-classified-moderation'] });
      setRejectingId(null);
      setReason('');
    },
    onError: (e: any) => alert(e.message ?? 'Failed to record decision.'),
  });

  if (isRoleLoading) return <div className="text-center py-10 text-gray-400">Loading…</div>;
  if (!isAdminOrOps) return <div className="text-center py-10 text-gray-500">Access restricted to Admin and Ops users.</div>;

  const loading = loadingBazaar || loadingClassified;

  return (
    <div className="page-shell-tight">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-900">Bazaar &amp; Classifieds Moderation</h1>
        <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-100 text-gray-700">
          {queue.length} pending
        </span>
      </div>

      <div className="flex gap-2 flex-wrap mb-4">
        {(['all', ModerationFlag.Clean, ModerationFlag.Abusive, ModerationFlag.Spam, ModerationFlag.Fraud] as const).map(f => (
          <button
            key={f}
            onClick={() => setFlagFilter(f)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
              flagFilter === f ? 'border-violet-600 bg-violet-50 text-violet-700' : 'border-gray-300 bg-white text-gray-700'
            }`}
          >
            {f === 'all' ? 'All' : FLAG_LABELS[f]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-400">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-3xl mb-2">✅</p>
          <p className="text-gray-400 text-sm">All clear! No listings waiting for review.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {visible.map(item => (
            <div key={`${item.source}-${item.id}`} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${FLAG_COLOURS[item.flag]}`}>
                  {FLAG_LABELS[item.flag]}
                </span>
                <span className="text-xs text-gray-400">{new Date(item.createdAt).toLocaleDateString('en-GB')}</span>
              </div>
              <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
              <p className="text-xs text-gray-500 mb-2">
                {item.source === 'bazaar'
                  ? (item.listingType != null ? 'For Sale/Wanted' : 'Bazaar')
                  : 'Classified'} · {item.sellerName}
              </p>
              <div className="bg-gray-50 rounded-lg p-2 text-xs text-gray-600 mb-3">
                <strong>AI Reason:</strong> {item.aiReason}
              </div>

              {rejectingId === item.id ? (
                <div className="flex flex-col gap-2">
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Reason for rejection"
                    className="text-sm border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => decisionMutation.mutate({ item, action: 'reject', reason })}
                      disabled={!reason.trim() || decisionMutation.isPending}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white disabled:opacity-50"
                    >
                      Confirm Reject
                    </button>
                    <button
                      onClick={() => { setRejectingId(null); setReason(''); }}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => decisionMutation.mutate({ item, action: 'approve' })}
                    disabled={decisionMutation.isPending}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-600 text-white disabled:opacity-50"
                  >
                    Approve &amp; Publish
                  </button>
                  <button
                    onClick={() => setRejectingId(item.id)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-300 text-red-600"
                  >
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
